import { useEffect, useId, useState } from "react";

import { ProfileAvatar, type PublicProfile } from "./ProfileAvatar";
type Actor = PublicProfile;
export function HandleInput({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (handle: string) => void;
  disabled: boolean;
}) {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [actors, setActors] = useState<Actor[]>([]);
  const [active, setActive] = useState(-1);
  const [status, setStatus] = useState<"idle" | "loading" | "ready" | "error">(
    "idle",
  );
  useEffect(() => {
    setActors([]);
    setActive(-1);
    setStatus("idle");
    const term = value.trim().replace(/^@/, "");
    if (!open || disabled || term.length < 2) return;
    let current = true;
    const controller = new AbortController();
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const timer = setTimeout(async () => {
      setStatus("loading");
      timeout = setTimeout(() => controller.abort(), 5000);
      try {
        const url = new URL(
          "https://public.api.bsky.app/xrpc/app.bsky.actor.searchActorsTypeahead",
        );
        url.searchParams.set("q", term);
        url.searchParams.set("limit", "5");
        const response = await fetch(url, {
          signal: controller.signal,
          credentials: "omit",
        });
        if (!response.ok) throw new Error("Lookup failed");
        const body: unknown = await response.json();
        if (
          !body ||
          typeof body !== "object" ||
          !("actors" in body) ||
          !Array.isArray(body.actors)
        )
          throw new Error("Invalid lookup");
        const results = body.actors
          .filter(
            (actor): actor is Actor =>
              !!actor &&
              typeof actor.did === "string" &&
              actor.did.startsWith("did:") &&
              typeof actor.handle === "string" &&
              actor.handle !== "handle.invalid" &&
              (actor.displayName === undefined ||
                typeof actor.displayName === "string"),
          )
          .slice(0, 5);
        if (current && !controller.signal.aborted) {
          setActors(results);
          setStatus("ready");
        }
      } catch {
        if (current) setStatus("error");
      } finally {
        clearTimeout(timeout);
      }
    }, 350);
    return () => {
      current = false;
      clearTimeout(timer);
      clearTimeout(timeout);
      controller.abort();
    };
  }, [value, open, disabled]);
  function choose(actor: Actor) {
    onChange(actor.handle);
    setOpen(false);
    setActive(-1);
  }
  return (
    <div
      className="handle-combobox"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
    >
      <input
        id="handle"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open && value.trim().length >= 2}
        aria-controls={listId}
        aria-activedescendant={
          open && active >= 0 ? `${listId}-${active}` : undefined
        }
        autoComplete="username"
        placeholder="you.bsky.social"
        value={value}
        disabled={disabled}
        onFocus={() => setOpen(true)}
        onChange={(event) => {
          onChange(event.target.value);
          setOpen(true);
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            setOpen(false);
            setActive(-1);
          } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            setOpen(true);
            if (actors.length)
              setActive((index) =>
                event.key === "ArrowDown"
                  ? (index + 1) % actors.length
                  : index <= 0
                    ? actors.length - 1
                    : index - 1,
              );
          } else if (
            event.key === "Enter" &&
            open &&
            active >= 0 &&
            actors[active]
          ) {
            event.preventDefault();
            choose(actors[active]);
          }
        }}
      />
      <div
        id={listId}
        role="listbox"
        aria-label="Suggested handles"
        hidden={!open || value.trim().length < 2}
      >
        {actors.map((actor, index) => (
          <button
            type="button"
            role="option"
            id={`${listId}-${index}`}
            key={actor.did}
            aria-selected={index === active}
            tabIndex={-1}
            onPointerDown={(event) => event.preventDefault()}
            onClick={() => choose(actor)}
          >
            <ProfileAvatar key={actor.avatar ?? "no-photo"} actor={actor} />
            <span className="handle-identity">
              <strong>{actor.displayName || actor.handle}</strong>
              <span className="handle-label">@{actor.handle}</span>
            </span>
          </button>
        ))}
      </div>
      {open && (
        <p className="muted" role="status">
          {status === "loading"
            ? "Finding handles…"
            : status === "error"
              ? "Suggestions unavailable. You can still enter your handle."
              : status === "ready" && !actors.length
                ? "No matches. You can still enter your handle."
                : ""}
        </p>
      )}
    </div>
  );
}
